import { useState } from "react";
import { C, FONT } from "./ui/theme";
import { TextField, SelectField, ValidationMessage, FormKitStyles, ProgressSteps, Button } from "./ui/FormKit";

// "Book a Demo" lead-capture modal — a 3-step form (details → company →
// schedule) that collects a real lead before a real scheduling link
// exists. The final "Book Demo" button is intentionally idle (no
// scheduling link/backend call yet, per request) — swap its onClick to
// open the real Calendly/HubSpot/etc. link (or POST the lead) once one
// exists. Everything before that final button — validation, step
// navigation — is fully functional.

const STEP_LABELS = ["Your details", "Your company", "Schedule"];

const COMPANY_SIZES = [
  { value: "", label: "Select company size" },
  { value: "1-10", label: "1–10 employees" },
  { value: "11-50", label: "11–50 employees" },
  { value: "51-200", label: "51–200 employees" },
  { value: "201-1000", label: "201–1,000 employees" },
  { value: "1000+", label: "1,000+ employees" },
];

const PREFERRED_TIMES = [
  { value: "none", label: "No preference" },
  { value: "morning", label: "Morning (9am–12pm)" },
  { value: "afternoon", label: "Afternoon (12pm–5pm)" },
  { value: "evening", label: "Evening (5pm–8pm)" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FIELDS = {
  firstName: "", lastName: "", email: "", phone: "",
  companyName: "", companySize: "", jobTitle: "",
  preferredTime: "none",
};

function validateStep(step, fields) {
  const e = {};
  if (step === 0) {
    if (!fields.firstName.trim()) e.firstName = "First name is required";
    if (!fields.lastName.trim()) e.lastName = "Last name is required";
    if (!fields.email.trim()) e.email = "Work email is required";
    else if (!EMAIL_RE.test(fields.email.trim())) e.email = "Enter a valid email address";
  } else if (step === 1) {
    if (!fields.companyName.trim()) e.companyName = "Company name is required";
  }
  return e;
}

export default function BookDemoModal({ onClose }) {
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));
  const err = (k) => (touched[k] ? errors[k] : undefined);

  const close = () => { onClose(); setStep(0); setFields(EMPTY_FIELDS); setTouched({}); setErrors({}); };

  const goNext = () => {
    const v = validateStep(step, fields);
    setErrors(v);
    if (step === 0) setTouched(t => ({ ...t, firstName: true, lastName: true, email: true }));
    if (step === 1) setTouched(t => ({ ...t, companyName: true }));
    if (Object.keys(v).length > 0) return;
    setStep(s => Math.min(s + 1, STEP_LABELS.length - 1));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={close}
    >
      <FormKitStyles />
      <div
        onClick={e => e.stopPropagation()}
        style={{ fontFamily: FONT, background: "#fff", borderRadius: 20, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 70px rgba(0,0,0,.3)", padding: "32px 32px 28px" }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-.3px", margin: "0 0 4px" }}>Book a Demo</h2>
            <p style={{ fontSize: 13.5, color: C.textSub, margin: 0 }}>Tell us a bit about you and we'll set up time with our team.</p>
          </div>
          <button type="button" onClick={close} aria-label="Close" style={{ background: C.bgAlt, border: "none", borderRadius: 8, width: 32, height: 32, fontSize: 16, color: C.textSub, cursor: "pointer", flexShrink: 0 }}>&#215;</button>
        </div>

        <div style={{ marginBottom: 26 }}>
          <ProgressSteps steps={STEP_LABELS} currentIndex={step} />
        </div>

        {step === 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <TextField id="bd-firstName" label="First name" placeholder="Jordan" value={fields.firstName} onChange={set("firstName")} error={err("firstName")} autoComplete="given-name" />
              <TextField id="bd-lastName" label="Last name" placeholder="Blake" value={fields.lastName} onChange={set("lastName")} error={err("lastName")} autoComplete="family-name" />
            </div>
            <TextField id="bd-email" type="email" label="Work email" placeholder="you@company.com" value={fields.email} onChange={set("email")} error={err("email")} autoComplete="email" />
            <TextField id="bd-phone" type="tel" label="Phone" optional placeholder="(555) 123-4567" value={fields.phone} onChange={set("phone")} autoComplete="tel" />
          </div>
        )}

        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <TextField id="bd-companyName" label="Company name" placeholder="Acme Inc." value={fields.companyName} onChange={set("companyName")} error={err("companyName")} autoComplete="organization" />
            <SelectField id="bd-companySize" label="Company size" optional value={fields.companySize} onChange={set("companySize")} options={COMPANY_SIZES} />
            <TextField id="bd-jobTitle" label="Job title" optional placeholder="Head of Sales" value={fields.jobTitle} onChange={set("jobTitle")} autoComplete="organization-title" />
          </div>
        )}

        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <SelectField id="bd-preferredTime" label="Preferred time" value={fields.preferredTime} onChange={set("preferredTime")} options={PREFERRED_TIMES} />

            <div style={{ background: C.bgAlt, borderRadius: 12, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 4 }}>Summary</p>
              <p style={{ fontSize: 13.5, color: C.text, margin: 0 }}>{fields.firstName} {fields.lastName} &middot; {fields.email}</p>
              <p style={{ fontSize: 13.5, color: C.text, margin: 0 }}>{fields.companyName}{fields.companySize ? ` · ${COMPANY_SIZES.find(o => o.value === fields.companySize)?.label}` : ""}</p>
            </div>

            {/* Intentionally idle — no real scheduling link/backend exists
                yet. Wire this onClick up to the real Calendly/HubSpot/etc.
                link (or a lead-capture POST) once one is available. */}
            <Button variant="primary" size="lg" onClick={() => {}} title="Demo booking coming soon" style={{ width: "100%" }}>
              Book Demo
            </Button>
            <ValidationMessage tone="muted">We're finishing our scheduling setup — this button will go live shortly.</ValidationMessage>
          </div>
        )}

        {step < 2 && (
          <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
            {step > 0 && (
              <Button variant="secondary" onClick={goBack} style={{ flex: "0 0 auto" }}>Back</Button>
            )}
            <Button variant="primary" onClick={goNext} style={{ flex: 1 }}>Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
